import { type FC, memo, Suspense, type JSX } from 'react';
import { View } from 'react-native';
import SvgImage from '../utils/svgImage';
import { type SvgImageProps } from '../utils/svgImageTypes';
import { type SvgProps } from 'react-native-svg';
import {
  SvgName,
  SvgNameCustomizable,
  SvgNameFixed,
  getSvgAsset,
  isSvgNameCustomizable
} from '@wre/infinity-svgs';
import getSvgDimens from '../utils/assetSvg/getSvgDimens';

/**
 * React component of an SVG obtained from assets. Uses the `name` prop to load a specific SVG.
 *
 * For providing the source, provide the `name` prop with a {@link SvgName} string.
 *
 * For sizing the SVG, do one of:
 * -   Provide width and/or height
 *     - If you provide just one, the image will calculate the other dimension based on its natural dimensions.
 * -   Don't provide `width` or `height`, and set `fill` to true (default value)
 *     - The image will fill its parent
 * -   Don't provide `width` or `height`, and set `fill` to false
 *     - The image will use its natural dimensions, as defined in the svg width/height props or viewbox
 *
 * If the SVG is one of {@link SvgNameCustomizable} (i.e. a .tsx file describing a react-native-svg), then you can
 * additionally pass in SVG props. Otherwise, they're ignored.
 */
const AssetSvg = memo(function ({
  name,
  width,
  height,
  style,
  fill = true,
  ...props
}: { width?: number; height?: number; fill?: boolean } & (
  | {
      name: SvgNameCustomizable;
      style?: SvgProps['style'];
      /** Svg props. Only used if `name` is one of {@link SvgNameCustomizable}, otherwise has no effect. */
      svgProps?: Omit<SvgProps, 'width' | 'height' | 'style'>;
    }
  | {
      name: SvgNameFixed;
      style?: SvgImageProps['style'];
    }
  | {
      name: SvgName;
      style?: SvgProps['style'] & SvgImageProps['style'];
    }
)): JSX.Element {
  const source = getSvgAsset(name);
  const { width: naturalWidth, height: naturalHeight } = getSvgDimens(name);

  if (!isSvgNameCustomizable(name)) {
    ////
    // SVG was a .svg file so name was a FixedAssetSvg.
    // Use expo-image or next-image to render
    ////
    if ('svgProps' in props && props.svgProps !== undefined) {
      console.warn('svgProps provided but name was a FixedSvgName');
    }

    if (width === undefined && height === undefined) {
      // Neither width nor height are provided.
      if (!fill) {
        // Show image using the width/height defined in the SVG file.
        return (
          <SvgImage
            source={source}
            width={naturalWidth}
            height={naturalHeight}
            style={style as SvgImageProps['style']}
          />
        );
      } else {
        // Allow image to fill its container
        return <SvgImage source={source} fill={true} style={style as SvgImageProps['style']} />;
      }
    } else {
      // At least one of width/height are given. If one is unset, calculate it based on the aspect ratio.
      const aspectRatio = naturalWidth / naturalHeight;
      if (width === undefined && height !== undefined) {
        // Height is given but width isn't - set width
        width = height * aspectRatio;
      } else if (width !== undefined && height === undefined) {
        // Width is given but height isn't - set height
        height = width / aspectRatio;
      }

      return (
        <SvgImage
          source={source}
          width={width}
          height={height}
          style={style as SvgImageProps['style']}
        />
      );
    }
  } else {
    ////
    // SVG was a .tsx file with react-native-svg, so name was a CustomizableSvgName.
    // Render as is, after applying the custom width/height.
    ////
    const svgProps = (props as { svgProps?: Omit<SvgProps, 'width' | 'height' | 'style'> })
      .svgProps;
    const Component = source as FC<SvgProps>;

    if (width === undefined && height === undefined) {
      // Neither width nor height are provided.
      if (!fill) {
        // Show image using the width/height defined in the SVG file.
        return (
          <Suspense fallback={<View style={{ width: naturalWidth, height: naturalHeight }} />}>
            <Component width={naturalWidth} height={naturalHeight} style={style} {...svgProps} />
          </Suspense>
        );
      } else {
        // Allow image to fill its container
        return (
          <Suspense fallback={<View style={{ flex: 1, alignSelf: 'stretch' }} />}>
            <Component width={undefined} height={undefined} style={style} {...svgProps} />
          </Suspense>
        );
      }
    } else {
      // At least one of width/height are given. If one is unset, calculate it based on the aspect ratio.
      const aspectRatio = naturalWidth / naturalHeight;
      if (width === undefined && height !== undefined) {
        // Height is given but width isn't - set width
        width = height * aspectRatio;
      } else if (width !== undefined && height === undefined) {
        // Width is given but height isn't - set height
        height = width / aspectRatio;
      }
    }

    return (
      <Suspense fallback={<View style={{ width: naturalWidth, height: naturalHeight }} />}>
        <Component width={width} height={height} style={style} {...svgProps} />
      </Suspense>
    );
  }
});

export { AssetSvg, getSvgDimens, type SvgName, type SvgNameFixed, type SvgNameCustomizable };
